import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Box, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { convertToGeoLocation } from "@src/appV2/Location";
import { requestFullLocationAccess } from "@src/appV2/Location/utils/permissions";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { getGradientByTimeSlot } from "@src/appV2/redesign/Shift/getGradientByTimeSlot";
import { ShiftHighlightsAndMapListItem } from "@src/appV2/redesign/Shift/HighlightsAndMapListItem";
import { ShiftWorkplaceListItem } from "@src/appV2/redesign/Shift/WorkplaceListItem";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import {
  List,
  ListItem,
  ListItemText,
  PageHeaderWrapper,
  Title,
} from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { formatTime } from "@src/appV2/redesign/utils/formatTime";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";
import { SHIFT_DETAILS_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { type ShiftPeriod, shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { generatePath, useHistory, useParams } from "react-router-dom";

import { PageContent } from "../../components/PageContent";
import { useGetShiftDetailsData } from "../api/useGetShiftDetailsData";
import { ParkingCardWithGeofence } from "../components/ParkingCard/ParkingCardWithGeofence";
import { LocationStatusRow } from "./LocationStatusRow";
import { useHyperTrackVerificationStatus } from "./useHyperTrackVerificationStatus";

export function LocationTrackingFailedPage() {
  const { shiftId } = useParams<{ shiftId: string }>();
  const { userId } = useDefinedWorker();
  const history = useHistory();

  const { shift, shiftState, isLoading, shiftError } = useGetShiftDetailsData({ shiftId });
  const { state, errorReason, retry } = useHyperTrackVerificationStatus({
    shiftId,
    workerId: userId,
  });

  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([
    shift?.agentReq ?? "",
  ]);

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_DETAILS_LOCATION_TRACKING_FAILED_PAGE_VIEWED,
    { shiftId, workerId: userId },
    { enabled: isDefined(shiftId) && isDefined(userId) }
  );

  function getHeaderTitle(): string {
    if (!isDefined(shift)) {
      return "";
    }

    const qualificationDisplayLabel =
      qualificationNameToDisplayLabelMap.get(shift.agentReq) ?? shift.agentReq;
    const formattedTime = formatTime(parseISO(shift.start), shift.facility.tmz);
    return `Your ${qualificationDisplayLabel} shift at ${formattedTime}`;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <PageHeaderWrapper sx={{ paddingTop: 0 }}>
          <Stack
            sx={(theme) => ({
              ...getSafeAreaPaddings({ top: true, fallbackTop: theme.spacing(3) }),
              paddingBottom: 7,
              paddingX: 6,
              transition: theme.transitions.create(["background"]),
              background: isDefined(shift?.name)
                ? getGradientByTimeSlot({
                    timeSlot: shiftPeriodToTimeSlot[shift.name as ShiftPeriod],
                    theme,
                  })
                : undefined,
            })}
          >
            <Stack spacing={6}>
              <IconButton
                iconType="chevron-left"
                variant="outlined"
                aria-label="Go back"
                onClick={() => {
                  history.replace(generatePath(SHIFT_DETAILS_FULL_PATH, { shiftId }));
                }}
              />
              <Title component="h3" variant="h3">
                {getHeaderTitle()}
              </Title>
            </Stack>
          </Stack>
        </PageHeaderWrapper>

        <PageContent>
          {isDefined(shiftError) && (
            <Alert severity="error" sx={{ margin: 4 }}>
              Something went wrong while loading shift details. Please try again.
            </Alert>
          )}

          {!isLoading && isDefined(shift) && isDefined(shiftState) && (
            <List>
              <ListItem divider>
                <ListItemText
                  disableTypography
                  primary={
                    <LocationStatusRow
                      state={state}
                      errorReason={errorReason}
                      action={
                        state === "failed" ? (
                          <TextButton
                            onClick={() => {
                              void requestFullLocationAccess();
                            }}
                          >
                            Go to settings
                          </TextButton>
                        ) : undefined
                      }
                    />
                  }
                />
              </ListItem>

              <ListItem divider>
                <ListItemText
                  disableTypography
                  primary={
                    <Box sx={{ "& .MuiCard-root": { width: "100%" } }}>
                      <ParkingCardWithGeofence
                        shift={shift}
                        workerId={userId}
                        shiftState={shiftState}
                      />
                    </Box>
                  }
                />
              </ListItem>

              <ShiftWorkplaceListItem
                workplaceId={shift.facilityId}
                workplaceName={shift.facility.name}
                workplaceType={shift.facility.type ?? ""}
                workplaceLocation={convertToGeoLocation(shift.facility.geoLocation.coordinates)}
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_WORKPLACE_CARD_TAPPED, {
                    shiftId: shift._id,
                    workplaceId: shift.facilityId,
                    sourceUI: "location-tracking-failed-page",
                  });

                  history.push(
                    generatePath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH, {
                      workplaceId: shift.facilityId,
                    })
                  );
                }}
              />
              <ShiftHighlightsAndMapListItem
                isMapEnabled
                workplaceId={shift.facilityId}
                workplaceName={shift.facility.name}
                workplaceLocation={convertToGeoLocation(shift.facility.geoLocation.coordinates)}
                workplaceFormattedAddress={shift.facility.fullAddress?.formatted}
                isDividerEnabled={false}
              />
            </List>
          )}
        </PageContent>

        {state === "failed" && (
          <PageFooterWrapper>
            <Button fullWidth variant="contained" size="large" onClick={retry}>
              Try again
            </Button>
          </PageFooterWrapper>
        )}

        {state === "connected" && (
          <PageFooterWrapper>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                history.replace(generatePath(SHIFT_DETAILS_FULL_PATH, { shiftId }));
              }}
            >
              Back to shift details
            </Button>
          </PageFooterWrapper>
        )}
      </PageWrapper>
    </ThemeProvider>
  );
}
